import moment from 'moment';
import {
  CLIENT_TYPE,
  DateFormat,
  Origin,
} from '@upstart/harmony/lib/constants';
import { getDateFormat } from '@upstart/harmony/lib/dealer-utils';
import { defaultPageTitle, pencilPageTitle } from './constants';

/**
 * Get CSS classname based on status string
 *
 * @param   {String}  statusString      status as returned by API
 * @return  {String}                    CSS classname corrosponding to statusString
 */
export const getValidationClassName = statusString => {
  let itemClassName;
  switch (statusString) {
    case 'success':
      itemClassName = 'status-success';
      break;
    case 'failure':
      itemClassName = 'status-error';
      break;
    case 'in-progress':
      itemClassName = 'status-running';
      break;
    default:
      itemClassName = '';
  }
  return itemClassName;
};

export const getDatePickerRangeOptions = () => ({
  Today: [moment().startOf('day'), moment().endOf('day')],
  Yesterday: [
    moment()
      .subtract(1, 'days')
      .startOf('day'),
    moment()
      .subtract(1, 'days')
      .endOf('day'),
  ],
  'Last 7 Days': [
    moment()
      .subtract(6, 'days')
      .startOf('day'),
    moment().endOf('day'),
  ],
  'Last 30 Days': [
    moment()
      .subtract(29, 'days')
      .startOf('day'),
    moment().endOf('day'),
  ],
  'This Month': [moment().startOf('month'), moment().endOf('month')],
  'Last Month': [
    moment()
      .subtract(1, 'month')
      .startOf('month'),
    moment()
      .subtract(1, 'month')
      .endOf('month'),
  ],
});

export const getDatePickerRangeLabel = (ranges, startDate, endDate) =>
  // find the range from the default list of ranges, using 'Custom Range' if none match
  (Object.entries(ranges).find(
    ([, [start, end]]) => startDate.isSame(start) && endDate.isSame(end),
  ) || ['Custom Range'])[0];

export const requiredValidationState = (value, submitted) => {
  if (!submitted) {
    return 'success';
  }
  return value && value.length > 0 ? 'success' : 'error';
};

export const requiredValidationStateObject = (value, submitted) => {
  if (!submitted) {
    return 'success';
  }
  if (!value || typeof value !== 'object') {
    return 'error';
  }
  return Object.keys(value).length > 0 ? 'success' : 'error';
};

export const maskPhone = text => {
  if (!text) {
    return text;
  }
  const p = text.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
  if (!p[2]) {
    return p[1];
  }

  const p1 = `(${p[1]}) `;
  const p2 = p[2];
  const p3 = p[3] ? `-${p[3]}` : '';
  return `${p1}${p2}${p3}`;
};

export const maskNumbers = text => (text ? text.replace(/\d/g, '*') : text);

export const removeHtmlTag = text =>
  text ? text.replace(/(<([^>]+)>)/gi, '') : text;

export const updateDocumentTitle = (path, count = 0, name = '') => {
  if (path?.search('pencil') >= 0) {
    if (count && name) {
      return `(${count}) ${name}'s ${pencilPageTitle}`;
    }
    if (count && !name) {
      return `(${count}) ${pencilPageTitle}`;
    }
    if (name && !count) {
      return `${name}'s ${pencilPageTitle}`;
    }
    return pencilPageTitle;
  }
  return defaultPageTitle;
};

export const updateDocumentFavIcon = active => {
  const favIcon = document.getElementById('favicon');
  const favIconLg = document.getElementById('favicon-lg');
  const favIconSm = document.getElementById('favicon-sm');
  if (active) {
    favIcon.href = '/favicon-active.ico';
    favIconLg.href = '/favicon-32x32-active.png';
    favIconSm.href = '/favicon-16x16-active.png';
  } else {
    favIcon.href = '/favicon.ico';
    favIconLg.href = '/favicon-32x32.png';
    favIconSm.href = '/favicon-16x16.png';
  }
};

export const getDateTimeFormat = dealer => {
  switch (getDateFormat(dealer)) {
    case DateFormat.DD_MM_YYYY:
      return ['D/M', 'h:mm A']; // August 12th 4PM => 12/8 4:00 PM
    case DateFormat.MM_DD_YYYY:
    default:
      return ['M/D', 'h:mm A']; // August 12th 4PM => 8/12 4:00 PM
  }
};

export const timestampToDealerTime = (value, dealer) =>
  moment(value).tz(dealer.timezone);

export const formatTimestampToDealerTime = (
  value,
  dealer,
  separator = ' ',
  reverse = false,
) => {
  const format = getDateTimeFormat(dealer);
  if (reverse) format.reverse();
  return timestampToDealerTime(value, dealer).format(format.join(separator));
};

export function exponentialBackoff(attempt, base = 1, maxBackoff = Infinity) {
  return Math.min(base * 2 ** (attempt + 1), maxBackoff);
}

export function getClientType(deal) {
  return deal?.origin === Origin.ONLINE
    ? CLIENT_TYPE.ONLINE
    : CLIENT_TYPE.IN_STORE;
}

export const filterAndJoinStrings = (input, separator = ', ') =>
  input.filter(e => !!e).join(separator);

export const isProduction = () => process.env.REACT_APP_ENV === 'production';

export const disableChangePasswordFunctionality = () => isProduction();
